// หน้า report ของ dataset ที่ประกาศ chartinfo.reportTab (ตอนนี้คือ melab_group)
//
// กติกาสำคัญ:
// - คอมโพเนนต์นี้ไม่รู้จักชื่อ dataset/การ์ดใด ๆ — ทุกอย่างมาจาก report_manifest.json
//   ที่ pipeline (jobs_base/melab_group) เป็นเจ้าของ การเพิ่ม/แก้การ์ด = แก้ manifest
// - SQL ประกอบด้วย str.replace ตามสัญญา __AXIS__/__ORDER__/__WHERE__/__BAR__
//   สูตรเดียวกับ fill() ใน jobs_base/melab_group/manifest.py — ห้ามแก้ข้างเดียว
//   (reconcile ฝั่ง build รัน SQL ชุดเดียวกัน จึงพิสูจน์ว่าเลขบนจอ = เลขที่ด่านตรวจ)
// - React 16.14-safe (submodule นี้ใช้ร่วมกับ host IHV3) · CSS ล้วน prefix ischart-
import { useCallback, useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { Download, RotateCcw, X } from "lucide-react";
import VegaEmbed from "./VegaEmbed";
import "./MelabReport.css";

type FilterValue = string | string[];

type CardView = {
  axis: string;
  filters: { [id: string]: FilterValue };
  // "stack" = แท่งซ้อนทาสีในแท่งเดียว · "dodge" = แท่งวางเทียบข้างกัน (Chart.tsx pattern)
  barMode: "stack" | "dodge";
  // id ของ groupOptions สำหรับการ์ดที่กลุ่มเลือกได้ ('' = การ์ดใช้กลุ่มตายตัว/ไม่มีกลุ่ม)
  group: string;
  // ช่วงวันที่ของการ์ดที่ประกาศ dateRange (การ์ด funnel) — YYYY-MM-DD
  dateStart?: string;
  dateEnd?: string;
};

type TableSort = { key: string; direction: "asc" | "desc" };

const todayIso = () => {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const quoteSql = (v: any) => `'${String(v).replace(/'/g, "''")}'`;

const joinClauses = (clauses: (string | null | undefined)[]) =>
  clauses.filter(Boolean).map((c) => ` AND (${c})`).join("");

// แทนที่ token ทุกตำแหน่ง — String.replace(string) แทนแค่ตัวแรก ถ้า token โผล่ซ้ำ
// จะเงียบ ๆ เหลือค้าง แล้ว SQL พัง (ฝั่ง python .replace แทนทุกตัว ต้องให้ตรงกัน)
const rep = (s: string, token: string, val: string) => s.split(token).join(val);

const fillSql = (
  tpl: string,
  opts: {
    axisExpr?: string; orderBy?: string; wheres?: (string | null | undefined)[];
    bars?: (string | null | undefined)[];
    groupSel?: string; groupBy?: string; dayStart?: string;
    dateStart?: string; dateEnd?: string;
  },
) => {
  let s = rep(tpl, "__AXIS__", opts.axisExpr ?? "");
  s = rep(s, "__ORDER__", opts.orderBy ?? "ORDER BY 1");
  s = rep(s, "__WHERE__", joinClauses(opts.wheres ?? []));
  // __BAR__ ใน auditSql = เงื่อนไขคลิกแท่งที่ต้องกรอง "ผลลัพธ์" หลัง GROUP (การ์ด 12)
  // — เข้าเป็น token แยกจาก __WHERE__ ซึ่งอยู่ชั้นใน · ไม่ส่ง bars = "" เหมือนเดิม
  s = rep(s, "__BAR__", joinClauses(opts.bars ?? []));
  s = rep(s, "__GROUPSEL__", opts.groupSel ?? "");
  s = rep(s, "__GROUPBY__", opts.groupBy ?? "1");
  // __DAYSTART__ อยู่ใน expr ของแกน (การ์ด funnel) — ต้องแทนหลัง __AXIS__ เสมอ
  // ค่ามาจาก dropdown จุดเริ่มเวลา (filter id 'daystart' — value คือชั่วโมงตรง ๆ)
  s = rep(s, "__DAYSTART__", opts.dayStart || "14");
  // ช่วงวันของโครงปฏิทิน (การ์ด funnel) — มาจาก date picker ตั้งแต่/ถึง
  s = rep(s, "__DATESTART__", opts.dateStart || "2026-08-06");
  s = rep(s, "__DATEEND__", opts.dateEnd || todayIso());
  return s;
};

// ค่า __DAYSTART__ ของการ์ด — มาจาก filter พิเศษ id 'daystart' (where ว่าง ใช้ค่าเป็น token)
const dayStartOf = (card: any, view: CardView) =>
  (card.filters || []).some((f: any) => f.id === "daystart")
    ? (Array.isArray(view.filters["daystart"])
      ? view.filters["daystart"][0] : view.filters["daystart"]) || "14" : "14";

// กลุ่มที่ active ของการ์ด: การ์ดกลุ่มเลือกได้ (groupOptions) ใช้ค่าใน view ·
// การ์ดกลุ่มตายตัวใช้ card.group · คืน null = ไม่มีกลุ่ม
const activeGroup = (card: any, view: CardView): any | null => {
  if (card.groupOptions) {
    const chosen = card.groupOptions.find((g: any) => g.id === view.group)
      || card.groupOptions[0];
    return chosen.column ? chosen : null;
  }
  return card.group || null;
};

// ช่วงเวลา derive จาก datetime แบบเดียวกับ cube (base1.sql) — ห้ามเปลี่ยนสูตรข้างเดียว
const periodClause = (period: string) => {
  if (period.startsWith("m:")) return `substring(datetime, 1, 7) = '${period.slice(2)}'`;
  if (period.startsWith("y:")) return `substring(datetime, 1, 4) = '${period.slice(2)}'`;
  return "";
};

// เปิดรายงานด้วยปีล่าสุด เพื่อให้ทุกการ์ดเห็นภาพรวมทั้งปีก่อน ผู้ใช้ยังสลับเป็นรายเดือนได้
// ถ้า manifest เก่าไม่มีรายการปี ให้ถอยไปใช้เดือนล่าสุดแทนเพื่อรักษา compatibility
const initialPeriod = (manifest: any) => {
  const years = manifest?.periods?.years || [];
  if (years.length) return `y:${years[years.length - 1].value}`;
  const months = manifest?.periods?.months || [];
  return months.length ? `m:${months[months.length - 1].value}` : "";
};

// แกนรายสัปดาห์ใช้ได้เฉพาะตอนดูทั้งปี — ตัวกรองช่วงข้อมูลตัดด้วย substring(datetime,1,7)
// พอเลือกเดือน สัปดาห์ที่คร่อมเดือนจะโดนผ่าครึ่ง แต่ป้าย (week_of_year) ยังประกาศตัวเป็น
// สัปดาห์เต็ม เช่น "Wk.31: 27 ก.ค. - 2 ส.ค." ใต้เดือน ส.ค. มีข้อมูลแค่ 1-2 ส.ค. — ตัวเลขถูก
// แต่ป้ายผิด ซึ่งอันตรายกว่าผิดทั้งคู่ จึงล็อกไม่ให้เลือก แล้วถอยไปรายวัน (วันไม่คร่อมเดือน)
// การ์ด allTime ไม่โดนกฎนี้ เพราะไม่ผูกกับตัวเลือกช่วงข้อมูลอยู่แล้ว (มี date picker ของตัวเอง)
const axisLocked = (card: any, axisId: string, period: string) =>
  !card.allTime && axisId === "week" && period.startsWith("m:");

const AXIS_LOCK_NOTE = "ดูได้เมื่อเลือกช่วงเป็นทั้งปี";

// แกนที่การ์ดควรเปิดมา ณ ช่วงข้อมูลนี้ — ถอยไปรายวันเมื่อแกนที่ขอมาโดนล็อก
const axisFor = (card: any, axisId: string, period: string) =>
  axisLocked(card, axisId, period) && (card.axes || []).some((a: any) => a.id === "day")
    ? "day" : axisId;

const defaultView = (card: any, period = ""): CardView => ({
  axis: axisFor(card, card.defaultAxis, period),
  filters: Object.fromEntries((card.filters || []).map((f: any) =>
    [f.id, f.multiple ? [f.default] : f.default])),
  barMode: card.defaultBarMode === "dodge" ? "dodge" : "stack",
  group: card.defaultGroup ?? "",
  ...(card.dateRange
    ? { dateStart: card.dateRange.start || "", dateEnd: todayIso() }
    : {}),
});

const isDefaultView = (card: any, view: CardView, period: string) => {
  const d = defaultView(card, period);
  return d.axis === view.axis && d.barMode === view.barMode && d.group === view.group &&
    d.dateStart === view.dateStart && d.dateEnd === view.dateEnd &&
    JSON.stringify(d.filters) === JSON.stringify(view.filters);
};

// สีของกลุ่มต้องไม่ซ้ำกัน — palette default ของ Vega มี 10 สีแล้ววนซ้ำ พอ group เป็น
// "รายการ/แพ็กเกจ" (20+ ค่า) คนละรายการจะได้สีเดียวกัน: ≤10 ใช้ default · ≤20 ใช้
// tableau20 · เกินนั้น generate hue ห่างเท่ากัน (ยืนยันไม่ซ้ำทุกจำนวน)
const colorScaleFor = (values: string[], order?: string[] | null) => {
  const domain = order && order.length >= values.length ? order : values;
  const n = domain.length;
  if (n <= 10) return order ? { domain: order } : undefined;
  if (n <= 20) return { domain, scheme: "tableau20" };
  const range = domain.map((_, i) =>
    `hsl(${Math.round((360 * i) / n)}, ${i % 2 ? 65 : 45}%, ${i % 2 ? 45 : 55}%)`);
  return { domain, range };
};

const safeFilePart = (s: string) =>
  String(s).replace(/[^0-9A-Za-z฀-๿]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 40);

const timestamp = () => {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}_${p(d.getHours())}${p(d.getMinutes())}`;
};

const compareTableValues = (left: any, right: any) => {
  const leftBlank = left === null || left === undefined || left === "";
  const rightBlank = right === null || right === undefined || right === "";
  if (leftBlank || rightBlank) return leftBlank === rightBlank ? 0 : leftBlank ? 1 : -1;
  const leftNumber = Number(left);
  const rightNumber = Number(right);
  if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) {
    return leftNumber - rightNumber;
  }
  return String(left).localeCompare(String(right), "th", {
    numeric: true,
    sensitivity: "base",
  });
};

const sortedTableRows = (rows: any[], sort?: TableSort) => {
  if (!sort) return rows;
  const direction = sort.direction === "asc" ? 1 : -1;
  return rows.map((row, index) => ({ row, index })).sort((a, b) => {
    const compared = compareTableValues(a.row[sort.key], b.row[sort.key]);
    return compared === 0 ? a.index - b.index : compared * direction;
  }).map(({ row }) => row);
};

const tableColumnClassName = (column: any) => [
  column.wrap && "ischart-melab-report-cell--wrap",
  column.compact && "ischart-melab-report-cell--compact",
].filter(Boolean).join(" ") || undefined;

const toSheet = (headers: string[], rows: any[][]) => {
  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  ws["!cols"] = headers.map((h, i) => ({
    wch: Math.min(40, Math.max(String(h).length + 2,
      ...rows.slice(0, 50).map((r) => String(r[i] ?? "").length + 2))),
  }));
  return ws;
};

// Vega ของหน้า report รับความกว้างเป็นตัวเลขได้เสถียรกว่า "container" ในทั้ง
// standalone และ host ที่ embed อยู่ จึงวัดการ์ดจริงทุกครั้งที่ขนาดหน้าจอเปลี่ยน
// แล้วส่งค่าให้ spec โดยตรง
function FullWidthVegaEmbed(props: any) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateWidth = () => {
      const nextWidth = Math.floor(container.getBoundingClientRect().width);
      setWidth((currentWidth) => currentWidth === nextWidth ? currentWidth : nextWidth);
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const { data, ...embedProps } = props;
  // spec อาจกำหนดความกว้างขั้นต่ำไว้เพื่อให้ป้ายแกนอ่านได้ครบ; ถ้าจอแคบกว่า
  // ให้กราฟกว้างเต็มการ์ดก่อน แล้วค่อยเลื่อนแนวนอนในกล่องกราฟ
  const requestedWidth = typeof data?.width === "number" ? data.width : 0;
  const chartWidth = Math.max(width, requestedWidth);
  return (
    <div ref={containerRef} style={{ width: "100%" }}>
      {width > 0 && <VegaEmbed {...embedProps} data={{ ...data, width: chartWidth }} />}
    </div>
  );
}

// ตัวกรองหลายค่าใช้ checkbox + ค้นหา เพราะ native <select multiple> ต้องกด Ctrl/Cmd
// และอ่านรายชื่อแพ็กเกจยาวหลายสิบค่าได้ยาก · option where ว่างคือขอบเขต "ทั้งหมด"
function MultiSelectFilter(props: {
  filter: any;
  value: FilterValue;
  className: string;
  onChange: (value: string[]) => void;
}) {
  const { filter, value, className, onChange } = props;
  const [search, setSearch] = useState("");
  const options = filter.options || [];
  const allOption = options.find((o: any) => !o.where);
  const selectable = options.filter((o: any) => !!o.where);
  const selected = (Array.isArray(value) ? value : [value]).filter(Boolean);
  const selectedReal = selected.filter((v) => selectable.some((o: any) => o.value === v));
  const visible = selectable.filter((o: any) =>
    String(o.label).toLocaleLowerCase("th").includes(search.trim().toLocaleLowerCase("th")));
  const summary = selectedReal.length === 0
    ? allOption?.label || "ทั้งหมด"
    : selectedReal.length === 1
      ? selectable.find((o: any) => o.value === selectedReal[0])?.label
      : `เลือกแล้ว ${selectedReal.length} รายการ`;

  const toggle = (optionValue: string) => {
    const next = selectedReal.includes(optionValue)
      ? selectedReal.filter((v) => v !== optionValue)
      : [...selectedReal, optionValue];
    onChange(next.length ? next : [allOption?.value || filter.default]);
  };

  return (
    <div className={className}>
      <span>{filter.label}</span>
      <details className="ischart-melab-report-multiselect">
        <summary title={String(summary)}>{summary}</summary>
        <div className="ischart-melab-report-multiselect-panel">
          <input type="search" value={search} placeholder="ค้นหาแพ็กเกจ…"
            aria-label={`ค้นหา${filter.label}`}
            onChange={(e) => setSearch(e.target.value)} />
          {allOption && (
            <label className="ischart-melab-report-multiselect-option ischart-melab-report-multiselect-option--all">
              <input type="checkbox" checked={selectedReal.length === 0}
                onChange={() => onChange([allOption.value])} />
              <span>{allOption.label}</span>
            </label>
          )}
          <div className="ischart-melab-report-multiselect-list">
            {visible.map((o: any) => (
              <label key={o.value} className="ischart-melab-report-multiselect-option">
                <input type="checkbox" checked={selectedReal.includes(o.value)}
                  onChange={() => toggle(o.value)} />
                <span>{o.label}</span>
              </label>
            ))}
            {!visible.length && <span className="ischart-melab-report-multiselect-empty">
              ไม่พบแพ็กเกจที่ค้นหา
            </span>}
          </div>
        </div>
      </details>
    </div>
  );
}

export default function MelabReport(props: any) {
  const { datasetId, onEvent, duckLoaded } = props;

  const [manifest, setManifest] = useState<any>(null);
  const [error, setError] = useState<string>("");
  const [period, setPeriod] = useState<string>("");
  const [views, setViews] = useState<{ [cardId: string]: CardView }>({});
  const [rowsByCard, setRowsByCard] = useState<{ [cardId: string]: any[] }>({});
  const [busyByCard, setBusyByCard] = useState<{ [cardId: string]: boolean }>({});
  const [sortByCard, setSortByCard] = useState<{ [cardId: string]: TableSort }>({});
  const [downloading, setDownloading] = useState<string>("");
  const [drill, setDrill] = useState<any>(null); // { title, columns, rows, busy }
  // การ์ดที่ประกาศ emptyProbeSql: true = ช่วงนี้ "ไม่มีข้อมูลต้นทางเลย" (ไม่ใช่แค่ผลกรองว่าง)
  const [noDataByCard, setNoDataByCard] = useState<{ [cardId: string]: boolean }>({});
  const unmounted = useRef(false);
  const requestVersion = useRef<{ [cardId: string]: number }>({});

  // Strict Mode (dev) จำลอง mount -> cleanup -> mount บน instance เดิม (ref ตัวเดิม)
  // ต้อง reset เป็น false ตอน setup ทุกครั้ง ไม่งั้นธงค้าง true ตั้งแต่รอบสอง
  // แล้ว setBundle/setRows ถูกทิ้งหมด — อาการ: ค้างที่ "กำลังโหลดข้อมูล report…" ตลอดกาล
  // (กับดักเดียวกับที่ MainInterface.ts DidMount ต้อง reset controller.unmounted)
  useEffect(() => {
    unmounted.current = false;
    return () => { unmounted.current = true; };
  }, []);

  const query = useCallback((sql: string) =>
    new Promise<any[]>((resolve, reject) => {
      onEvent({
        message: "QueryMelabReport",
        params: { sql, setRows: resolve, setError: (m: string) => reject(new Error(m)) },
      });
    }), [onEvent]);

  // โหลด manifest + ลงทะเบียน parquet ครั้งเดียว
  useEffect(() => {
    if (!duckLoaded || !datasetId) return;
    onEvent({
      message: "GetMelabReportBundle",
      params: {
        datasetId,
        setBundle: (m: any) => {
          if (unmounted.current) return;
          setManifest(m);
          const p0 = initialPeriod(m);
          setPeriod(p0);
          setViews(Object.fromEntries(
            (m?.cards || []).map((c: any) => [c.id, defaultView(c, p0)])));
        },
        setError: (msg: string) => { if (!unmounted.current) setError(msg); },
      },
    });
  }, [duckLoaded, datasetId, onEvent]);

  const wheresFor = useCallback((card: any, view: CardView) => {
    const clauses: (string | null)[] = [];
    if (!card.allTime && period) clauses.push(periodClause(period));
    // ช่วงวันที่ของการ์ด (dateRange) — expr มาจาก manifest (นิยามวันตามขอบที่เลื่อน)
    // ค่ามาจาก <input type="date"> จึงเป็น YYYY-MM-DD เสมอ ไม่ต้อง escape
    if (card.dateRange?.expr) {
      if (view.dateStart) clauses.push(`${card.dateRange.expr} >= '${view.dateStart}'`);
      if (view.dateEnd) clauses.push(`${card.dateRange.expr} <= '${view.dateEnd}'`);
    }
    for (const f of card.filters || []) {
      const raw = view.filters[f.id];
      if (f.multiple) {
        const selected = Array.isArray(raw) ? raw : [raw];
        const chosen = (f.options || []).filter((o: any) =>
          selected.includes(o.value) && !!o.where);
        if (chosen.length) {
          clauses.push(chosen.map((o: any) => `(${o.where})`).join(" OR "));
        }
      } else {
        const chosen = (f.options || []).find((o: any) => o.value === raw);
        if (chosen?.where) clauses.push(chosen.where);
      }
    }
    return clauses;
  }, [period]);

  const loadCard = useCallback(async (card: any, view: CardView) => {
    const axis = (card.axes || []).find((a: any) => a.id === view.axis) || card.axes?.[0];
    if (!axis) return;
    // multi-select เปลี่ยนได้ติดกันหลายครั้ง: รับเฉพาะผล query ล่าสุดของการ์ด
    // ป้องกัน query เก่าที่ช้ากว่ากลับมาเขียนทับชุด checkbox ใหม่
    const version = (requestVersion.current[card.id] || 0) + 1;
    requestVersion.current[card.id] = version;
    setBusyByCard((b) => ({ ...b, [card.id]: true }));
    try {
      const grp = activeGroup(card, view);
      const selectable = !!card.groupOptions;
      // group option อาจมี SQL ของมุมตัวเอง เมื่อ grain ต้องเปลี่ยนจริง ๆ
      // (เช่น การ์ด 12: คน → คน-แพ็กเกจ) ไม่ใช่เพียงเติมคอลัมน์สีใน query เดิม
      const sql = fillSql(grp?.chartSql || card.chartSql, {
        axisExpr: axis.expr,
        orderBy: axis.orderBy || "ORDER BY 1",
        wheres: wheresFor(card, view),
        groupSel: selectable && grp?.column ? `, ${grp.column} AS grp` : "",
        groupBy: selectable && grp?.column ? "1, 2" : "1",
        dayStart: dayStartOf(card, view),
        dateStart: view.dateStart, dateEnd: view.dateEnd,
      });
      const rows = await query(sql);
      // (2026-09-01) กันไฟเขียวปลอม: การ์ดว่าง + ประกาศ emptyProbeSql → เช็คว่าช่วงนี้
      // มีข้อมูลต้นทางไหม (เช่นการ์ด 7.1 เดือนไร้ใบเสร็จ ห้ามอ่านว่า "ทุกใบปกติ")
      let noData = false;
      if (rows.length === 0 && card.emptyProbeSql) {
        try {
          const probe = await query(fillSql(card.emptyProbeSql, {
            wheres: wheresFor(card, view),
            dayStart: dayStartOf(card, view),
            dateStart: view.dateStart, dateEnd: view.dateEnd }));
          noData = Number(probe?.[0]?.n ?? 0) === 0;
        } catch { noData = false; }
      }
      if (!unmounted.current && requestVersion.current[card.id] === version) {
        setRowsByCard((r) => ({ ...r, [card.id]: rows }));
        setNoDataByCard((m) => ({ ...m, [card.id]: noData }));
      }
    } catch (e: any) {
      if (!unmounted.current && requestVersion.current[card.id] === version) {
        setError(String(e?.message ?? e));
      }
    } finally {
      if (!unmounted.current && requestVersion.current[card.id] === version) {
        setBusyByCard((b) => ({ ...b, [card.id]: false }));
      }
    }
  }, [query, wheresFor]);

  // โหลดทุกการ์ดเมื่อ manifest/ช่วงเวลาเปลี่ยน · การ์ด allTime ไม่ต้องโหลดซ้ำตอนเปลี่ยนช่วง
  const lastPeriod = useRef<string>("");
  useEffect(() => {
    if (!manifest || !period) return;
    const periodChangedOnly = lastPeriod.current !== "" && lastPeriod.current !== period;
    lastPeriod.current = period;
    // ระดับช่วงเปลี่ยน (ปี↔เดือน) แล้วแกนที่ค้างอยู่โดนล็อก → ถอยไปรายวันแล้วจำค่าใหม่ไว้
    // ต้องทำก่อน loadCard ไม่งั้นยิง SQL มุมที่เพิ่งห้าม และ dropdown จะโชว์ค่าที่กดไม่ได้
    const moved: { [cardId: string]: CardView } = {};
    for (const card of manifest.cards || []) {
      if (periodChangedOnly && card.allTime) continue;
      const cur = views[card.id] || defaultView(card, period);
      const axis = axisFor(card, cur.axis, period);
      const next = axis === cur.axis ? cur : { ...cur, axis };
      if (next !== cur) moved[card.id] = next;
      loadCard(card, next);
    }
    if (Object.keys(moved).length) setViews((v) => ({ ...v, ...moved }));
    // views ไม่อยู่ใน deps — การเปลี่ยน view รายการ์ดโหลดผ่าน setView ด้านล่าง
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manifest, period]);

  const setView = (card: any, next: CardView) => {
    setViews((v) => ({ ...v, [card.id]: next }));
    loadCard(card, next);
  };

  const toggleTableSort = (cardId: string, key: string) => {
    setSortByCard((current) => {
      const active = current[cardId];
      return {
        ...current,
        [cardId]: {
          key,
          direction: active?.key === key && active.direction === "asc" ? "desc" : "asc",
        },
      };
    });
  };

  const toggleDrillSort = (key: string) => {
    setDrill((current: any) => {
      if (!current?.sortable) return current;
      const active = current.sort as TableSort | undefined;
      return {
        ...current,
        sort: {
          key,
          direction: active?.key === key && active.direction === "asc" ? "desc" : "asc",
        },
      };
    });
  };

  const renderFilterControl = (card: any, view: CardView, filter: any) => {
    const className = [
      "ischart-melab-report-control",
      filter.wide && "ischart-melab-report-control--wide",
      filter.id === "package" && "ischart-melab-report-control--package",
      filter.newRow && "ischart-melab-report-control--new-row",
    ].filter(Boolean).join(" ");
    if (filter.multiple) {
      return <MultiSelectFilter key={filter.id} filter={filter}
        className={className} value={view.filters[filter.id]}
        onChange={(value) => setView(card,
          { ...view, filters: { ...view.filters, [filter.id]: value } })} />;
    }
    return (
      <label key={filter.id} className={className}>
        <span>{filter.label}</span>
        <select className={filter.id === "package"
          ? "ischart-melab-report-select--package" : undefined}
          value={String(view.filters[filter.id] ?? "")}
          onChange={(e) => setView(card,
            { ...view, filters: { ...view.filters, [filter.id]: e.target.value } })}>
          {(filter.options || []).map((o: any) =>
            <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </label>
    );
  };

  // ---------- Vega spec ----------
  // โครง layered (bar + text ป้ายผลรวมปลายแท่ง) ตามแพตเทิร์น Chart.tsx:288-368
  // - ไม่ใส่ $schema + width เป็นตัวเลข (vega-lite ที่ติดตั้งเป็น v6 ประกาศ v5 แล้วเตือน/ไม่ render)
  // - ป้ายตัวเลขใช้ aggregate sum และต้องใส่ title ซ้ำให้ตรงกับแกนหลัก ไม่งั้น Vega
  //   รวม layer แล้วหัวแกนกลายเป็น "value, Sum of value" (กติกาเดียวกับคอมเมนต์ใน Chart.tsx)
  const specFor = (card: any, view: CardView, rows: any[]) => {
    const axis = (card.axes || []).find((a: any) => a.id === view.axis) || card.axes?.[0];
    const grpDef = activeGroup(card, view);
    const hasGroup = !!grpDef;
    const horizontal = grpDef?.horizontal ?? !!card.wide;
    const valueTitle = card.valueLabel || "ค่า";
    const bucketTitle = grpDef?.bucketLabel || axis?.label || "";
    const guideConfig = {
      style: {
        "guide-label": { fontSize: 13, lineBreak: "\n" },
        "guide-title": { fontSize: 14 },
      },
    };
    const bucketField = { field: "bucket", type: "nominal" as const, sort: null as any };
    if (grpDef?.bucketOrder) bucketField.sort = grpDef.bucketOrder;
    else if (axis?.domain) bucketField.sort = axis.domain;
    // ป้ายแกน X ของกราฟตามเวลาต้องอ่านได้ตรง ๆ: ปรับให้แนวนอนและแตกบรรทัด
    // เช่น "Wk.27: 29 มิ.ย. - 5 ก.ค. 69" เป็น 3 บรรทัด แทนการเอียงตัวเล็ก
    const timeAxis = {
      title: bucketTitle,
      labelAngle: 0,
      labelFontSize: 16,
      labelLineHeight: 20,
      labelLimit: 0,
      labelOverlap: false,
      labelPadding: 8,
      labelExpr: "replace(replace(datum.label, ': ', ':\\n'), ' - ', '\\n')",
    };
    // labelLimit: ป้ายแนวนอนยาวได้ (การ์ด 9 มีช่วงขาย+จำนวนคนต่อท้าย, การ์ด 4 ชื่อรายการยาว)
    const bucketEnc = horizontal
      ? { ...bucketField, axis: { title: bucketTitle, labelLimit: 300 } }
      : { ...bucketField, axis: timeAxis };
    const valueEnc = { field: "value", type: "quantitative" as const,
      axis: { title: valueTitle } };
    const sumEnc = { aggregate: "sum", field: "value", type: "quantitative" as const,
      title: valueTitle };

    // Gantt ปฏิทินโปร — หน้าตาตาม design เก่า (OLD_FAVOR_BACKUP §ปฏิทินโปรโมชั่น)
    // โค้ดเขียนใหม่: 1 แถวข้อมูล = 1 โปร แท่งลาก bar_start→bar_end สีตามสถานะการขาย
    // ข้อความ "N ใบ · M คน" ท้ายแท่ง · ลำดับแถวมาจาก ORDER BY ของ SQL (วันเปิดขายจริง)
    // spec นี้ compile+render ผ่าน vega-lite/vega ที่ติดตั้งจริงแบบ headless แล้ว
    if (card.chartType === "calendar") {
      const names = Array.from(new Set(rows.map((r) => String(r.bucket))));
      const grpValues = Array.from(new Set(rows.map((r) => String(r.grp)))).sort();
      const colorScale = colorScaleFor(grpValues, grpDef?.order);
      return {
        width: 640,
        height: Math.max(240, names.length * 26),
        config: guideConfig,
        data: { values: rows },
        encoding: {
          y: { field: "bucket", type: "ordinal", sort: names,
               axis: { title: axis?.label || "โปรโมชั่น", labelLimit: 230 } },
          // แกนวันที่ ไม่บังคับ zero — ไล่ตามช่วงขายจริง
          x: { field: "bar_start", type: "temporal",
               axis: { title: "ช่วงเวลา", format: "%b %Y", tickCount: "month" } },
        },
        layer: [
          {
            mark: { type: "bar", cornerRadius: 2, height: 12, tooltip: true },
            encoding: {
              x2: { field: "bar_end" },
              color: { field: "grp", type: "nominal",
                       legend: { title: grpDef?.seriesLabel || grpDef?.label || "" },
                       ...(colorScale ? { scale: colorScale } : {}) },
            },
          },
          {
            mark: { type: "text", align: "left", baseline: "middle", dx: 6,
                    fontSize: 13, color: "#6b7280" },
            encoding: {
              x: { field: "bar_end", type: "temporal" },
              text: { field: "note", type: "nominal" },
            },
          },
        ],
      };
    }

    const barEncoding: any = {
      tooltip: [
        { field: "bucket", type: "nominal", title: bucketTitle || "แกน" },
        ...(hasGroup ? [{ field: "grp", type: "nominal",
          title: grpDef!.seriesLabel || grpDef!.label || "กลุ่ม" }] : []),
        { field: "value", type: "quantitative", title: valueTitle, format: "," },
      ],
    };
    if (hasGroup) {
      const grpValues = Array.from(new Set(rows.map((r) => String(r.grp)))).sort();
      const colorScale = colorScaleFor(grpValues, grpDef!.order);
      barEncoding.color = {
        field: "grp", type: "nominal",
        legend: { title: grpDef!.seriesLabel || grpDef!.label || "" },
        ...(colorScale ? { scale: colorScale } : {}),
      };
    }

    const buckets = new Set(rows.map((r) => r.bucket)).size;
    const nGroups = hasGroup ? new Set(rows.map((r) => r.grp)).size : 1;
    const dodged = hasGroup && view.barMode === "dodge";

    // แท่งเทียบข้างกัน: offset ต้องอยู่ฝั่งแกน ordinal เท่านั้น (แนวตั้ง = xOffset,
    // แนวนอน = yOffset) — ใส่ผิดฝั่ง Vega ทิ้ง channel เงียบ ๆ (กับดักจาก Chart.tsx:356-361)
    const offsetEnc: any = { field: "grp", type: "nominal" };
    if (grpDef?.order) offsetEnc.sort = grpDef.order;
    if (dodged) {
      barEncoding[horizontal ? "yOffset" : "xOffset"] = offsetEnc;
    }
    const barMark: any = { type: "bar", tooltip: true };
    if (dodged) barMark[horizontal ? "height" : "width"] = { band: 0.9 };

    // FullWidthVegaEmbed วัดความกว้างจริงของการ์ดแล้วแทนค่า width นี้ก่อน render
    // ป้ายเวลาแบบ 3 บรรทัดต้องมีความกว้างต่อช่วงพออ่านได้; ถ้าป้ายเยอะให้เลื่อนในกรอบกราฟ
    const baseWidth = card.wide ? 1100 : 560;
    const labelWidth = axis?.id === "week" ? 140 : axis?.id === "day" ? 96 : 80;
    const width = !horizontal
      ? Math.max(baseWidth, buckets * labelWidth)
      : baseWidth;
    const baseHeight = horizontal ? Math.max(220, buckets * 24) : 280;
    const height = dodged && horizontal
      ? Math.max(baseHeight, buckets * (nGroups * 16 + 8)) : baseHeight;

    return {
      width,
      height,
      // Vega ต้องประกาศตัวแบ่งบรรทัดใน guide style จึงจะแสดง \n จาก labelExpr
      // เป็นหลายบรรทัดจริง แทนการตัดคำแล้วใส่ …
      config: guideConfig,
      data: { values: rows },
      encoding: horizontal
        ? { y: bucketEnc, x: valueEnc }
        : { x: bucketEnc, y: valueEnc },
      layer: [
        { mark: barMark, encoding: barEncoding },
        // ป้ายตัวเลขปลายแท่ง (แนวนอน = ขวาแท่ง / แนวตั้ง = เหนือแท่ง)
        // โหมดซ้อน = ผลรวมต่อ bucket · โหมดเทียบข้างกัน = ค่าของแต่ละแท่งเหนือแท่งตัวเอง
        // (เดิม dodge ยังใช้ผลรวม ทำให้เลขรวมลอยที่ความสูงแบบซ้อนเหมือนค้างจากโหมดเก่า
        //  — ผู้ใช้รายงาน 2026-08-12 · ป้ายต่อแท่งต้องได้ offset เดียวกับแท่งด้วย)
        {
          mark: {
            type: "text",
            ...(horizontal ? { dx: 5, align: "left" } : { dy: -5 }),
            fontWeight: "bold", fontSize: 14, color: "black",
          },
          encoding: dodged
            ? {
                text: { field: "value", type: "quantitative", format: "," },
                ...(horizontal ? { x: valueEnc } : { y: valueEnc }),
                [horizontal ? "yOffset" : "xOffset"]: offsetEnc,
              }
            : {
                text: { ...sumEnc, format: "," },
                ...(horizontal ? { x: sumEnc } : { y: sumEnc }),
              },
        },
      ],
    };
  };

  // ---------- คลิกแท่ง → หน้าต่างลอยแถวเบื้องหลังแท่ง ----------
  const openDrill = async (card: any, datum: any) => {
    if (datum?.bucket === undefined) return;
    const view = views[card.id] || defaultView(card, period);
    const axis = (card.axes || []).find((a: any) => a.id === view.axis) || card.axes?.[0];
    const grpVal = datum.grp;
    let sql: string;
    let columns: any[];
    if (card.drill) {
      // drill ข้าม sheet (เช่น ปฏิทิน → รายใบจาก orders) — expr มาจาก manifest
      // barAxisDatum = ชื่อ field ค่าดิบในแถวข้อมูล เมื่อ bucket เป็นป้ายตกแต่ง (การ์ด 9)
      const bucketVal = card.drill.barAxisDatum
        ? datum[card.drill.barAxisDatum] : datum.bucket;
      // (แก้ 2026-09-01) drill ต้องโดนตัวกรองช่วงเวลา/filter เหมือนตัวแท่ง — เดิมแทนแค่
      // __BAR__ ทำให้เลือกเดือนแล้วคลิกได้แถวทั้งปี (แท่ง 0 ใบ แต่ drill 5 แถว)
      sql = fillSql(card.drill.sql, {
        wheres: wheresFor(card, view),
        bars: [
          `${card.drill.barAxisExpr} = ${quoteSql(bucketVal)}`,
          grpVal !== undefined && card.drill.barGroupExpr
            ? `${card.drill.barGroupExpr} = ${quoteSql(grpVal)}` : null,
        ],
        dayStart: dayStartOf(card, view),
        dateStart: view.dateStart, dateEnd: view.dateEnd });
      columns = card.drill.columns;
    } else {
      const grpDef = activeGroup(card, view);
      const auditTemplate = grpDef?.auditSql || card.auditSql;
      const bars: (string | null)[] = [
        `${grpDef?.barAxisExpr || axis.expr} = ${quoteSql(datum.bucket)}`,
      ];
      // กลุ่มเป็น expr ได้ (เช่น CASE ของการ์ด 3) — ครอบวงเล็บแล้วเทียบตรง ๆ
      if (grpVal !== undefined && grpDef?.column) {
        bars.push(`(${grpDef.barGroupExpr || grpDef.column}) = ${quoteSql(grpVal)}`);
      }
      // การ์ดที่ auditSql มี __BAR__ (การ์ด 12 — bucket เป็นค่าระดับคนหลัง GROUP):
      // เงื่อนไขคลิกแท่งต้องกรองผลลัพธ์ชั้นนอก ไม่ใช่แถว fact ชั้นใน — ส่งผ่าน token แยก
      // (เดิมยัดรวม __WHERE__ แล้ว drill ขัดกับแท่ง — เจอจริง 2026-08-31)
      const viaBarToken = String(auditTemplate).includes("__BAR__");
      sql = fillSql(auditTemplate, {
        wheres: viaBarToken ? wheresFor(card, view) : [...wheresFor(card, view), ...bars],
        bars: viaBarToken ? bars : undefined,
        dayStart: dayStartOf(card, view),
        dateStart: view.dateStart, dateEnd: view.dateEnd });
      // มุมที่เปลี่ยน grain ประกาศคอลัมน์ของตัวเองได้ (การ์ด 12 มุม Package)
      columns = grpDef?.auditColumns || card.auditColumns;
    }
    const title = `${card.title} — ${datum.bucket}${grpVal !== undefined ? ` · ${grpVal}` : ""}`;
    setDrill({
      title,
      columns,
      rows: [],
      busy: true,
      cardId: card.id,
      // (2026-09-01) เปิด sort ให้ทุกหน้าต่าง drill — เครื่อง sort มีครบอยู่แล้ว
      // auditDefaultSort ของการ์ด (ถ้าประกาศ) เป็นลำดับแรกเปิด
      sortable: true,
      sort: card.auditDefaultSort,
    });
    try {
      const rows = await query(sql);
      if (!unmounted.current) setDrill((d: any) =>
        d && d.title === title ? { ...d, rows, busy: false } : d);
    } catch (e: any) {
      if (!unmounted.current) { setError(String(e?.message ?? e)); setDrill(null); }
    }
  };

  // ---------- Download ----------
  const download = async (card: any, kind: "excel" | "audit") => {
    if (downloading) return;
    setDownloading(`${card.id}:${kind}`);
    try {
      const view = views[card.id] || defaultView(card, period);
      const axis = (card.axes || []).find((a: any) => a.id === view.axis) || card.axes?.[0];
      const wb = XLSX.utils.book_new();
      if (kind === "excel") {
        const rawRows = rowsByCard[card.id] || [];
        const rows = card.sortable
          ? sortedTableRows(rawRows, sortByCard[card.id]) : rawRows;
        if (card.chartType === "table" && card.tableColumns) {
          // การ์ดแบบตาราง: ส่งออกทุกคอลัมน์ตามที่เห็นบนจอ
          const headers = card.tableColumns.map((c: any) => c.label);
          const body = rows.map((r: any) =>
            card.tableColumns.map((c: any) => r[c.key] ?? ""));
          XLSX.utils.book_append_sheet(wb, toSheet(headers, body), "Summary");
        } else {
        const grpDef = activeGroup(card, view);
        // (2026-09-01) มุมที่เปลี่ยน bucket (เช่น การ์ด 12 แบ่งตาม Package) ต้องใช้ป้าย
        // ของมุมนั้น — เดิมใช้ป้ายแกน ทำให้หัวคอลัมน์ไฟล์ Excel สลับความหมาย
        const headers = [grpDef?.bucketLabel || axis?.label || "แกน",
          ...(grpDef ? [grpDef.label || "กลุ่ม"] : []),
          card.valueLabel || "ค่า"];
        const body = rows.map((r) =>
          grpDef ? [r.bucket, r.grp, r.value] : [r.bucket, r.value]);
        XLSX.utils.book_append_sheet(wb, toSheet(headers, body), "Summary");
        }
      } else {
        const grpDef = activeGroup(card, view);
        const sql = fillSql(grpDef?.auditSql || card.auditSql, { wheres: wheresFor(card, view),
          dayStart: dayStartOf(card, view),
          dateStart: view.dateStart, dateEnd: view.dateEnd });
        const rows = await query(sql);
        const headers = card.auditColumns.map((c: any) => c.label);
        const body = rows.map((r: any) => card.auditColumns.map((c: any) => r[c.key] ?? ""));
        XLSX.utils.book_append_sheet(wb, toSheet(headers, body), "Audit");
      }
      const label = period.startsWith("m:") || period.startsWith("y:") ? period.slice(2) : "all";
      // exportName (มากับ manifest ตั้งตอน build) = ชื่อไฟล์ตามเวลา "สร้างข้อมูล"
      // ไม่ใช่เวลากดโหลด — ใช้กับปุ่ม Excel เท่านั้น ปุ่ม Audit คงชื่อกลางเดิม
      XLSX.writeFile(wb, kind === "excel" && card.exportName
        ? `${card.exportName}.xls`
        : `melab_report_${safeFilePart(card.id)}_${kind}_${label}_${timestamp()}.xlsx`);
    } catch (e: any) {
      setError(String(e?.message ?? e));
    } finally {
      setDownloading("");
    }
  };

  const downloadDrill = async () => {
    if (!drill || drill.busy || downloading) return;
    setDownloading("drill");
    try {
      const wb = XLSX.utils.book_new();
      const headers = drill.columns.map((c: any) => c.label);
      const rows = drill.sortable ? sortedTableRows(drill.rows, drill.sort) : drill.rows;
      const body = rows.map((r: any) => drill.columns.map((c: any) => r[c.key] ?? ""));
      XLSX.utils.book_append_sheet(wb, toSheet(headers, body), "Rows");
      XLSX.writeFile(wb, `melab_report_drill_${safeFilePart(drill.cardId)}_${timestamp()}.xlsx`);
    } finally {
      setDownloading("");
    }
  };

  // ---------- render ----------
  if (error) {
    return <section className="ischart-melab-report">
      <p className="ischart-melab-report-error">โหลดหน้า report ไม่สำเร็จ: {error}</p>
    </section>;
  }
  if (!manifest) {
    return <section className="ischart-melab-report">
      <div className="ischart-melab-report-loading">กำลังโหลดข้อมูล report…</div>
    </section>;
  }

  const periodOptions = [
    ...(manifest.periods?.years || []).map((y: any) => ({ value: `y:${y.value}`, label: y.label })),
    ...(manifest.periods?.months || []).map((m: any) => ({ value: `m:${m.value}`, label: m.label })),
  ];
  const drillRows = drill?.sortable
    ? sortedTableRows(drill.rows || [], drill.sort) : drill?.rows || [];

  return (
    <section className="ischart-melab-report">
      <div className="ischart-melab-report-heading">
        <div>
          <h2>{manifest.title}</h2>
          <p className="ischart-melab-report-scope">{manifest.scopeNote}</p>
          <p className="ischart-melab-report-built">ข้อมูล ณ {manifest.builtAt}</p>
        </div>
      </div>
      <div className="ischart-melab-report-period-dock">
        <label className="ischart-melab-report-period">
          <span>ช่วงข้อมูล</span>
          <select value={period} onChange={(e) => setPeriod(e.target.value)}>
            {periodOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
      </div>

      <div className="ischart-melab-report-grid">
        {(manifest.cards || []).map((card: any) => {
          const view = views[card.id] || defaultView(card, period);
          const rows = rowsByCard[card.id] || [];
          const tableRows = card.sortable
            ? sortedTableRows(rows, sortByCard[card.id]) : rows;
          const busy = !!busyByCard[card.id];
          const axisSelectable = (card.axes || []).length > 1;
          return (
            <section key={card.id}
              className={`ischart-melab-report-card${card.wide ? " ischart-melab-report-card--wide" : ""}`}>
              <div className="ischart-melab-report-card-head">
                <h3>{card.title}</h3>
                <span className="ischart-melab-report-chip">
                  {activeGroup(card, view)?.grain || card.grain}
                </span>
                {card.allTime &&
                  <span className="ischart-melab-report-chip ischart-melab-report-chip--scope">
                    ไม่ขึ้นกับช่วงเวลาที่เลือก
                  </span>}
              </div>
              <p className="ischart-melab-report-subtitle">{card.subtitle}</p>
              <div className="ischart-melab-report-toolbar">
                <div className="ischart-melab-report-controls">
                  {axisSelectable && (
                    <label>
                      <span>มิติ</span>
                      <select value={view.axis}
                        onChange={(e) => setView(card, { ...view, axis: e.target.value })}>
                        {card.axes.map((a: any) => {
                          // แกนที่ล็อกยังต้องโผล่ให้เห็น (กฎบ้านนี้: ขอบเขตต้องอยู่บนหน้าจอ)
                          // ถ้าหายไปเฉย ๆ คนจะเดาว่าระบบพัง แทนที่จะรู้ว่าเป็นการตัดสินใจ
                          const locked = axisLocked(card, a.id, period);
                          return (
                            <option key={a.id} value={a.id} disabled={locked}>
                              {locked ? `${a.label} (${AXIS_LOCK_NOTE})` : a.label}
                            </option>
                          );
                        })}
                      </select>
                    </label>
                  )}
                  {(card.filters || []).filter((f: any) => !f.afterDateRange)
                    .map((f: any) => renderFilterControl(card, view, f))}
                  {/* ช่วงวันที่ของการ์ด (dateRange ใน manifest) — นิยามวันตามขอบเวลาที่เลื่อน */}
                  {card.dateRange && (
                    <>
                      <label>
                        <span>ตั้งแต่</span>
                        <input type="date" value={view.dateStart || ""}
                          onChange={(e) => setView(card, { ...view, dateStart: e.target.value })} />
                      </label>
                      <label>
                        <span>ถึง</span>
                        <input type="date" value={view.dateEnd || ""}
                          onChange={(e) => setView(card, { ...view, dateEnd: e.target.value })} />
                      </label>
                    </>
                  )}
                  {(card.filters || []).filter((f: any) => f.afterDateRange)
                    .map((f: any) => renderFilterControl(card, view, f))}
                  {/* กลุ่มเลือกได้ (การ์ดที่ประกาศ groupOptions ใน manifest) — เปลี่ยนแล้ว query ใหม่ */}
                  {card.groupOptions && (
                    <label>
                      <span>{card.groupLabel || "กลุ่ม"}</span>
                      <select value={view.group}
                        onChange={(e) => setView(card, { ...view, group: e.target.value })}>
                        {card.groupOptions.map((g: any) =>
                          <option key={g.id} value={g.id}>{g.label}</option>)}
                      </select>
                    </label>
                  )}
                  {/* แท่งเทียบข้างกัน — เฉพาะตอนมีกลุ่ม active (ไม่มีกลุ่ม = ไม่มีอะไรให้เทียบ)
                      สลับ mode ไม่ต้อง query ใหม่ (ข้อมูลเดิม เปลี่ยนแค่ spec)
                      Gantt (calendar) ไม่มีโหมดแท่งให้สลับ */}
                  {card.chartType !== "calendar" && activeGroup(card, view) && (
                    <label>
                      <span>รูปแบบแท่ง</span>
                      <button type="button"
                        className={`ischart-melab-report-btn ischart-melab-report-btn--toggle${view.barMode === "dodge" ? " active" : ""}`}
                        onClick={() => setViews((v) => ({
                          ...v,
                          [card.id]: { ...view, barMode: view.barMode === "dodge" ? "stack" : "dodge" },
                        }))}>
                        {view.barMode === "dodge" ? "เทียบข้างกัน ✓" : "เทียบข้างกัน"}
                      </button>
                    </label>
                  )}
                </div>
                <div className="ischart-melab-report-actions">
                  <button type="button" title="กลับค่าเริ่มต้น"
                    className="ischart-melab-report-btn ischart-melab-report-btn--icon"
                    disabled={isDefaultView(card, view, period)}
                    onClick={() => setView(card, defaultView(card, period))}>
                    <RotateCcw size={14} />
                  </button>
                  <button type="button" className="ischart-melab-report-btn"
                    disabled={busy || !!downloading}
                    onClick={() => download(card, "excel")}>
                    <Download size={14} />
                    {downloading === `${card.id}:excel` ? "กำลังสร้าง…" : "Excel"}
                  </button>
                  <button type="button" className="ischart-melab-report-btn"
                    disabled={busy || !!downloading}
                    onClick={() => download(card, "audit")}>
                    <Download size={14} />
                    {downloading === `${card.id}:audit` ? "กำลังสร้าง…" : "Audit"}
                  </button>
                </div>
              </div>
              <div className={`ischart-melab-report-chart${busy ? " ischart-melab-report-chart--busy" : ""}`}>
                {rows.length ? (
                  card.chartType === "table" ? (
                    /* การ์ดแบบตาราง (เช่น 7.1 ยอดบิลที่มีปัญหา) — ตารางคือรายละเอียดแล้ว
                       ไม่มี drill · โครง/สไตล์ตารางเดียวกับหน้าต่าง drill */
                    <table className="ischart-melab-report-table">
                      <thead>
                        <tr>{(card.tableColumns || []).map((c: any) => {
                          const activeSort = sortByCard[card.id];
                          const active = activeSort?.key === c.key;
                          return <th key={c.key} className={tableColumnClassName(c)}
                            aria-sort={active
                              ? activeSort.direction === "asc" ? "ascending" : "descending"
                              : undefined}>
                            {card.sortable ? (
                              <button type="button" className="ischart-melab-report-sort"
                                onClick={() => toggleTableSort(card.id, c.key)}>
                                <span>{c.label}</span>
                                <span aria-hidden="true" className="ischart-melab-report-sort-icon">
                                  {active ? activeSort.direction === "asc" ? "▲" : "▼" : "↕"}
                                </span>
                              </button>
                            ) : c.label}
                          </th>;
                        })}</tr>
                      </thead>
                      <tbody>
                        {tableRows.map((r: any, i: number) => (
                          <tr key={i}>{(card.tableColumns || []).map((c: any) =>
                            <td key={c.key} className={tableColumnClassName(c)}>
                              {String(r[c.key] ?? "")}</td>)}</tr>
                        ))}
                      </tbody>
                      {/* แถวรวม — บวกจากแถวที่แสดงจริง (คอลัมน์ตาม tableTotals ใน manifest) */}
                      {card.tableTotals && (
                        <tfoot>
                          <tr>
                            {(card.tableColumns || []).map((c: any, i: number) => (
                              <td key={c.key} style={{ fontWeight: "bold" }}>
                                {i === 0 ? "รวม"
                                  : card.tableTotals.includes(c.key)
                                    ? rows.reduce((s: number, r: any) =>
                                        s + (Number(r[c.key]) || 0), 0).toLocaleString()
                                    : ""}
                              </td>
                            ))}
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  ) : (
                  <FullWidthVegaEmbed
                    data={specFor(card, view, rows)}
                    eventListener={{
                      type: "click",
                      handler: (_ev: any, item: any) => {
                        if (item?.datum) openDrill(card, item.datum);
                      },
                    }}
                  />
                  )
                ) : (
                  <div className="ischart-melab-report-empty">
                    {/* ข้อความว่างมาจาก manifest (key emptyText) — เดิม hardcode ข้อความของ
                        การ์ด 7.1 ให้การ์ดตารางทุกใบ ทำให้การ์ดอื่นขึ้นข้อความผิดเรื่อง
                        (validate.py บังคับให้การ์ดตารางทุกใบประกาศ emptyText เอง) */}
                    {busy ? "กำลังรวมข้อมูล…"
                      : (noDataByCard[card.id] && card.emptyTextNoData)
                        || card.emptyText || "ไม่มีข้อมูลในช่วงที่เลือก"}
                  </div>
                )}
              </div>
              {/* เวลาสร้างข้อมูลของการ์ด (runner ประทับตอน build — การ์ด funnel) */}
              {card.dataAsOf && (
                <p className="ischart-melab-report-auditgrain">
                  ข้อมูลสร้างเมื่อ {card.dataAsOf}
                </p>
              )}
              <p className="ischart-melab-report-auditgrain">Audit:{" "}
                {activeGroup(card, view)?.auditGrain || card.auditGrain}</p>
            </section>
          );
        })}
      </div>

      {drill && (
        <div className="ischart-melab-report-drill" onClick={() => setDrill(null)}>
          <div className="ischart-melab-report-drill-panel" onClick={(e) => e.stopPropagation()}>
            <div className="ischart-melab-report-drill-head">
              <h3>{drill.title}</h3>
              <div className="ischart-melab-report-actions">
                <button type="button" className="ischart-melab-report-btn"
                  disabled={drill.busy || !!downloading} onClick={downloadDrill}>
                  <Download size={14} />
                  {downloading === "drill" ? "กำลังสร้าง…" : "Excel"}
                </button>
                <button type="button" title="ปิด"
                  className="ischart-melab-report-btn ischart-melab-report-btn--icon"
                  onClick={() => setDrill(null)}>
                  <X size={14} />
                </button>
              </div>
            </div>
            {drill.busy ? (
              <div className="ischart-melab-report-loading">กำลังดึงแถว…</div>
            ) : (
              <div className="ischart-melab-report-drill-tablewrap">
                <table className="ischart-melab-report-table">
                  <thead>
                    <tr><th className="ischart-melab-report-rowno">No.</th>
                    {drill.columns.map((c: any) => {
                      const active = drill.sort?.key === c.key;
                      return <th key={c.key} className={tableColumnClassName(c)}
                        aria-sort={active
                          ? drill.sort.direction === "asc" ? "ascending" : "descending"
                          : undefined}>
                        {drill.sortable ? (
                          <button type="button" className="ischart-melab-report-sort"
                            onClick={() => toggleDrillSort(c.key)}>
                            <span>{c.label}</span>
                            <span aria-hidden="true" className="ischart-melab-report-sort-icon">
                              {active ? drill.sort.direction === "asc" ? "▲" : "▼" : "↕"}
                            </span>
                          </button>
                        ) : c.label}
                      </th>;
                    })}</tr>
                  </thead>
                  <tbody>
                    {drillRows.map((r: any, i: number) => (
                      <tr key={i}>
                        <td className="ischart-melab-report-rowno">{i + 1}</td>
                        {drill.columns.map((c: any) =>
                          <td key={c.key} className={tableColumnClassName(c)}>
                            {String(r[c.key] ?? "")}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="ischart-melab-report-drill-count">{drill.rows.length} แถว</p>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
